import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { TranslationService } from '@mirage-projects/cw-commons';
import { PcvizVectorLayerUtils, Property, PropertySet } from '@mirage-projects/cw-models';
import { GisPcvizService } from '@mirage-projects/cw-pcviz-core';

import { PropertyBagService } from '../property-bag.service';

/** This component is responsible to show select property */
@Component({
    selector: 'cw-property-text',
    templateUrl: './property-text.component.html',
    styleUrls: ['./property-text.component.scss'],
    standalone: false,
})
export class PropertyTextComponent implements OnInit, OnDestroy {
    /** This object comes from the core and provides methods to obtain and set the value of the properties */
    @Input() propertySet!: PropertySet;

    /** Input field with the required information for this field type */
    @Input() property!: Property;

    vectorLayerUtils!: PcvizVectorLayerUtils;
    label = '';

    private propertyValue: any;
    private onDestroy$: Subject<void> = new Subject();

    constructor(
        private propertyBagService: PropertyBagService,
        private translationService: TranslationService,
        private gisPcvizService: GisPcvizService
    ) {}

    ngOnInit(): void {
        this.label = this.getCorrectLabel();
        this.vectorLayerUtils = this.gisPcvizService.getVectorLayerUtils();
        this.initValue();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    initValue(): void {
        this.property.value = this.propertyBagService.getValueProperty(this.propertySet, this.property);

        // FIXME: Remove this condition when we support FileType in the propertybag(server and front)
        const isFile =
            this.property.label === 'file_name' &&
            (this.propertySet.getLayerCategory() === this.vectorLayerUtils.Categories.PLANAR_IMAGE ||
                this.propertySet.getLayerCategory() === this.vectorLayerUtils.Categories.PANORAMA);

        if (isFile) {
            this.property.value = this.getFileNameFromPath(this.property.value, this.propertySet.getLayerCategory());
            return;
        }

        this.subscribeToLanguageChanged();
        this.doTextTranslation();
    }

    getFileNameFromPath(path: string, category: string): string {
        const name = path.substring(path.lastIndexOf('/') + 1);
        let extensionIndex = -1;
        if (category === this.vectorLayerUtils.Categories.PANORAMA) {
            extensionIndex = name.lastIndexOf('.panorama');
        }
        if (category === this.vectorLayerUtils.Categories.PLANAR_IMAGE) {
            extensionIndex = name.lastIndexOf('.planarimage');
        }
        if (extensionIndex !== -1) {
            return name.substring(0, extensionIndex);
        }
        return name;
    }

    onChange(): void {
        this.propertyBagService.setValueProperty(this.propertySet, this.property);
    }

    private getCorrectLabel(): string {
        return this.property.label.startsWith('VECTOR_LAYER_PROPERTIES')
            ? this.property.label
            : this.propertyBagService.getCorrectLabel(this.property);
    }

    private subscribeToLanguageChanged(): void {
        this.propertyValue = this.property.value;
        this.translationService.onChangeLanguage$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.doTextTranslation();
        });
    }

    private doTextTranslation(): void {
        if (this.property.translate) {
            const keyFeature = `FEATURE_PROPERTIES.${this.propertyValue}`;
            this.property.value = this.translationService.translateWithDefault(keyFeature, this.propertyValue);
        }
    }
}
